/**
 * Logger Utility
 * 
 * Production-safe logging that:
 * - Writes to sessionStorage for evidence/debugging
 * - Silent in production builds (no console output)
 * - Provides structured logging with timestamps
 * 
 * @module utils/logger
 */

const IS_PRODUCTION = typeof process !== 'undefined' && 
                      typeof process.env !== 'undefined' && 
                      process.env.NODE_ENV === 'production';

const MAX_LOGS = 100;

class Logger {
  constructor(namespace) {
    this.namespace = namespace;
    this.storageKey = `wms_logs_${namespace}`;
  }

  _writeToStorage(level, message, data) {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    try {
      const logs = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
      logs.push({
        timestamp: new Date().toISOString(),
        level,
        namespace: this.namespace,
        message,
        data,
      });

      const trimmedLogs = logs.slice(-MAX_LOGS);
      sessionStorage.setItem(this.storageKey, JSON.stringify(trimmedLogs));
    } catch (error) { /* noop */ }
  }

  log(message, data) {
    this._writeToStorage('log', message, data);
    if (!IS_PRODUCTION) {
      // eslint-disable-next-line no-console
      console.log(`[${this.namespace}]`, message, data || '');
    }
  }

  info(message, data) {
    this._writeToStorage('info', message, data);
    if (!IS_PRODUCTION) {
      // eslint-disable-next-line no-console
      console.info(`[${this.namespace}]`, message, data || '');
    }
  }

  warn(message, data) {
    this._writeToStorage('warn', message, data);
    // eslint-disable-next-line no-console
    console.warn(`[${this.namespace}]`, message, data || '');
  }

  debug(message, data) {
    this._writeToStorage('debug', message, data);
    if (!IS_PRODUCTION) {
      // eslint-disable-next-line no-console
      console.debug(`[${this.namespace}]`, message, data || '');
    }
  }

  error(message, data) {
    this._writeToStorage('error', message, data);
    // eslint-disable-next-line no-console
    console.error(`[${this.namespace}]`, message, data || '');
  }

  exportLogs() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return [];
    }

    try {
      return JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
    } catch (error) {
      return [];
    }
  }

  clearLogs() {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem(this.storageKey);
    }
  }
}

/**
 * Create a logger instance for a specific namespace
 * @param {string} namespace - Logger namespace (e.g., 'chatbot', 'api', 'auth')
 * @returns {Logger} Logger instance
 */
export function createLogger(namespace) {
  return new Logger(namespace);
}

/**
 * Export all logs from all namespaces
 * @returns {Object} All logs grouped by namespace
 */
export function exportAllLogs() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return {};
  }

  const allLogs = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('wms_logs_')) {
      const namespace = key.replace('wms_logs_', '');
      try {
        allLogs[namespace] = JSON.parse(sessionStorage.getItem(key) || '[]');
      } catch (error) { /* noop */ }
    }
  }

  return allLogs;
}

/**
 * Clear all logs from all namespaces
 */
export function clearAllLogs() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('wms_logs_')) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => sessionStorage.removeItem(key));
}

export default { createLogger, exportAllLogs, clearAllLogs };
